/**
 * Venice AI client — https://docs.venice.ai
 *
 * Runs in the browser with the user's own Venice API key (same trust model as
 * the OpenAI and Google keys). Covers the three surfaces the canvas uses:
 *
 *   images  POST /image/generate      text-to-image (base64 JSON response)
 *           POST /image/multi-edit    reference/edit models, 1..N input images (binary response)
 *   video   POST /video/queue         start an async job (Seedance and friends)
 *           POST /video/retrieve      poll; JSON while PROCESSING, video/mp4 when COMPLETED
 *           POST /video/quote         price estimate in USD
 *           POST /video/complete      optional storage cleanup after download
 *
 * Model ids and per-model constraints live in models/config.ts and mirror
 * GET https://api.venice.ai/api/v1/models?type=image|inpaint|video.
 */

import { VENICE_MAX_REQUEST_BYTES, formatMegabytes, requestBytes } from './veniceRequestSize';

export const VENICE_API_BASE = 'https://api.venice.ai/api/v1';

export class VeniceError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'VeniceError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Seedance consent attestation, only needed when Venice answers with a needs_consent response. */
export interface VeniceSeedanceConsents {
  confirmed_terms_and_privacy: true;
  confirmed_legal_right: true;
  confirmed_screening_acknowledged: true;
}

// ── helpers ──────────────────────────────────────────────────────────────────

function authHeaders(apiKey: string): Record<string, string> {
  return { Authorization: `Bearer ${apiKey}` };
}

function extractMessage(body: any, fallback: string): string {
  if (!body) return fallback;
  const candidates = [
    body?.error?.message,
    typeof body?.error === 'string' ? body.error : undefined,
    body?.message,
    body?.details?.message,
    typeof body?.details === 'string' ? body.details : undefined,
  ].filter((v) => typeof v === 'string' && v.trim());
  if (candidates.length) return candidates[0] as string;
  try {
    return JSON.stringify(body).slice(0, 240);
  } catch {
    return fallback;
  }
}

async function readBody(res: Response): Promise<{ text: string; json: any }> {
  const text = await res.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  return { text, json };
}

function looksLikeConsentRequest(json: any): boolean {
  if (!json || typeof json !== 'object') return false;
  if (json.needs_consent || json.error?.needs_consent) return true;
  const code = json.code || json.error?.code || json.error?.type;
  if (typeof code === 'string' && /consent/i.test(code)) return true;
  const msg = extractMessage(json, '');
  return /needs[_ ]consent|consent (is )?required/i.test(msg);
}

const FIELD_NAMES: Record<string, string> = {
  prompt: 'The prompt',
  negative_prompt: 'The negative prompt',
  duration: 'The duration',
  aspect_ratio: 'The aspect ratio',
  resolution: 'The resolution',
  audio: 'The audio setting',
  image_url: 'The start frame',
  end_image_url: 'The end frame',
  reference_image_urls: 'The reference pictures',
  elements: 'The elements',
  scene_image_urls: 'The scene pictures',
};

/**
 * A 400 "Invalid request parameters" lists what was wrong in `issues` (zod style:
 * { path, message, code, maximum }); say which fields, in words where we can.
 */
function describeIssues(json: any): string {
  const issues: any[] = Array.isArray(json?.issues) ? json.issues : [];
  const parts = issues.slice(0, 3).map((issue) => {
    const path = Array.isArray(issue?.path) ? issue.path.join('.') : '';
    const field = FIELD_NAMES[path];
    if (field && issue?.code === 'too_big' && typeof issue.maximum === 'number' && /prompt$/.test(path)) {
      return `${field} can be at most ${issue.maximum.toLocaleString('en-US')} characters for this model.`;
    }
    const message = typeof issue?.message === 'string' ? issue.message.replace(/\.$/, '') : 'not accepted';
    return `${field ?? (path || 'The request')}: ${message}.`;
  });
  if (issues.length > 3) parts.push(`And ${issues.length - 3} more.`);
  return parts.join(' ');
}

function toVeniceError(res: Response, json: any, text: string): VeniceError {
  const code = json?.error?.code || json?.code || json?.error?.type;
  if (looksLikeConsentRequest(json)) {
    // 409 needs_consent body: { error: {code, message}, consent_flow, consent: { consent_version, policy_text }, docs_url }
    const policy =
      json?.consent?.policy_text || json?.policy_text || json?.error?.policy_text || json?.needs_consent?.policy_text;
    const fallback = extractMessage(json, '') || 'This model requires you to confirm you have the legal right to use the submitted media.';
    return new VeniceError(typeof policy === 'string' && policy.trim() ? policy : fallback, res.status, 'needs_consent', json);
  }
  if (res.status === 401) return new VeniceError('Venice API key is invalid or missing. Check Settings → API Keys.', 401, code, json);
  if (res.status === 402) return new VeniceError('Your Venice balance is too low for this request. Top up at venice.ai.', 402, code, json);
  if (res.status === 429) return new VeniceError('Venice rate limit reached. Wait a moment and try again.', 429, code, json);
  // Timeouts: Venice's own (408, 504) and Cloudflare's in front of it (524), which answers with an HTML page
  if (res.status === 408 || res.status === 504 || res.status === 524) {
    return new VeniceError(`Venice ran out of time before answering (${res.status}). Try again; large images take longest, so 2K or a lower quality may help.`, res.status, code || 'timeout', json);
  }
  // Any other error page in HTML: its title, not its markup
  if (!json && /^\s*</.test(text)) {
    const title = /<title>([^<]*)<\/title>/i.exec(text)?.[1]?.trim() || /<h1>([^<]*)<\/h1>/i.exec(text)?.[1]?.trim();
    return new VeniceError(`Venice API error (${res.status}): ${title || res.statusText || 'the service answered with an error page'}`, res.status, code, json);
  }
  let msg = extractMessage(json, text.slice(0, 240) || res.statusText || 'Unknown error');
  const issues = describeIssues(json);
  if (issues) msg = `${msg.replace(/\.$/, '')}. ${issues}`;
  if (json?.error?.type === 'provider_content_policy') {
    // Terminal provider rejection; Venice refunds and may suggest an alternative model.
    if (json.error.credits_refunded) msg += ' Credits were refunded.';
    if (json.error.recommended_model) msg += ` Try the ${json.error.recommended_model} model instead.`;
  }
  return new VeniceError(`Venice API error (${res.status}): ${msg}`, res.status, code, json);
}

/** A failure after this long is a request that ran out of time, not a connection that never worked. */
const SLOW_FAILURE_MS = 60 * 1000;

/** For image requests: what to try when one ran out of time. */
const IMAGE_SLOW_HINT = 'Large images take longest: try 2K or a lower quality, or fewer images at once.';

/** A dropped connection, worded by how long the request had been running. */
const connectionLost = (startedAt: number, slowHint?: string, quick = 'Could not reach Venice. Check your connection, and whether a browser extension or network filter blocks api.venice.ai.'): VeniceError => {
  const elapsed = Date.now() - startedAt;
  if (elapsed < SLOW_FAILURE_MS) return new VeniceError(quick, 0, 'network');
  return new VeniceError(
    `Venice worked on this for ${Math.round(elapsed / 1000)} seconds, then the connection was closed before an answer came back.${slowHint ? ` ${slowHint}` : ''}`,
    0,
    'timeout',
  );
};

/**
 * POST a JSON body. Venice's front door refuses bodies of 32 MB or more without CORS headers,
 * a request that runs too long is cut off the same way, and a blocked or dropped connection
 * also reaches the page as a bare "Failed to fetch"; each becomes a message that says what happened.
 */
async function postJson(path: string, apiKey: string, body: Record<string, unknown>, slowHint?: string): Promise<{ res: Response; startedAt: number }> {
  const payload = JSON.stringify(body);
  const size = requestBytes(payload);
  if (size >= VENICE_MAX_REQUEST_BYTES) {
    throw new VeniceError(
      `This request is ${formatMegabytes(size)} with its pictures, clips and audio, and Venice accepts at most 32 MB. Use fewer or smaller references, or a shorter clip.`,
      413,
      'request_too_large',
    );
  }
  const startedAt = Date.now();
  try {
    const res = await fetch(`${VENICE_API_BASE}${path}`, {
      method: 'POST',
      headers: { ...authHeaders(apiKey), 'Content-Type': 'application/json' },
      body: payload,
    });
    return { res, startedAt };
  } catch (e) {
    if ((e as { name?: string } | null)?.name === 'AbortError') throw e;
    const large = size > 8 * 1024 * 1024 ? ` The request was ${formatMegabytes(size)}, so a slow upload may have been cut off.` : '';
    throw connectionLost(startedAt, slowHint, `Could not reach Venice. Check your connection, and whether a browser extension or network filter blocks api.venice.ai.${large}`);
  }
}

/** Reads a response body; a connection that drops while the answer is coming in says so. */
async function readOrLost<T>(read: () => Promise<T>, startedAt: number, slowHint?: string): Promise<T> {
  try {
    return await read();
  } catch (e) {
    if (!(e instanceof TypeError)) throw e;
    throw connectionLost(startedAt, slowHint, 'The connection closed while the answer was coming back from Venice. Try again.');
  }
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

/** Reads natural dimensions of an image data URL (used to pre-validate video reference frames). */
export function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Could not decode reference image'));
    img.src = dataUrl;
  });
}

/**
 * Seedance reference-image rules from GET /models?type=video:
 * short side ≥ 300px and aspect ratio between 0.4 and 2.5.
 */
export async function assertSeedanceReferenceImage(dataUrl: string, label = 'Reference image'): Promise<void> {
  const { width, height } = await getImageDimensions(dataUrl);
  const shortSide = Math.min(width, height);
  const ratio = width / height;
  if (shortSide < 300) {
    throw new VeniceError(`${label} is too small (${width}×${height}). Venice's video models need the short side to be at least 300px.`, 400, 'reference_too_small');
  }
  if (ratio < 0.4 || ratio > 2.5) {
    throw new VeniceError(`${label} aspect ratio ${ratio.toFixed(2)} is outside the 0.4–2.5 range Venice's video models accept.`, 400, 'reference_bad_aspect');
  }
}

// ── images ───────────────────────────────────────────────────────────────────

export interface VeniceImageGenerateParams {
  apiKey: string;
  model: string;
  prompt: string;
  negativePrompt?: string;
  /** e.g. "16:9" — only for models that list aspectRatios */
  aspectRatio?: string;
  /** "1K" | "2K" | "4K" — only for resolution-tier models (Nano Banana Pro, GPT Image 2) */
  resolution?: string;
  /** "low" | "medium" | "high" — only GPT Image 2 family */
  quality?: 'low' | 'medium' | 'high' | string;
  /** 1–4 images in one call */
  variants?: number;
  seed?: number;
  format?: 'png' | 'jpeg' | 'webp';
  hideWatermark?: boolean;
  /** Venice blurs pictures it classes as adult content unless this is false (utils/veniceSafeMode.ts) */
  safeMode?: boolean;
}

/** Text-to-image. Returns data URLs. */
export async function veniceGenerateImage(p: VeniceImageGenerateParams): Promise<string[]> {
  const format = p.format || 'png';
  const body: Record<string, unknown> = {
    model: p.model,
    prompt: p.prompt,
    format,
    return_binary: false,
    hide_watermark: p.hideWatermark ?? true,
    safe_mode: p.safeMode ?? true,
  };
  if (p.negativePrompt) body.negative_prompt = p.negativePrompt;
  if (p.aspectRatio) body.aspect_ratio = p.aspectRatio;
  if (p.resolution) body.resolution = p.resolution;
  if (p.quality) body.quality = p.quality;
  if (p.variants && p.variants > 1) body.variants = Math.min(4, Math.max(1, Math.floor(p.variants)));
  if (typeof p.seed === 'number') body.seed = p.seed;

  const { res, startedAt } = await postJson('/image/generate', p.apiKey, body, IMAGE_SLOW_HINT);
  const { text, json } = await readOrLost(() => readBody(res), startedAt, IMAGE_SLOW_HINT);
  if (!res.ok) throw toVeniceError(res, json, text);

  const images: unknown[] = Array.isArray(json?.images) ? json.images : [];
  const out = images
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .map((b64) => (b64.startsWith('data:') ? b64 : `data:image/${format};base64,${b64}`));
  if (!out.length) {
    throw new VeniceError('Venice returned no image data. The prompt may have been blocked by safety filters.', res.status, 'no_images', json);
  }
  return out;
}

export interface VeniceImageEditParams {
  apiKey: string;
  /** The edit/inpaint model id, e.g. "nano-banana-pro-edit" or "gpt-image-2-edit" */
  model: string;
  prompt: string;
  /** 1..maxInputImages data URLs; the first is the base image, the rest are references/layers */
  images: string[];
  aspectRatio?: string;
  resolution?: string;
  quality?: string;
  format?: 'png' | 'jpeg' | 'webp';
  /** Venice blurs pictures it classes as adult content unless this is false (utils/veniceSafeMode.ts) */
  safeMode?: boolean;
}

/**
 * Image editing / image-to-image with one or more input images.
 * Uses /image/multi-edit for every case (it accepts a single image too and, unlike
 * /image/edit, exposes the GPT Image `quality` knob). Returns one data URL.
 */
export async function veniceEditImage(p: VeniceImageEditParams): Promise<string> {
  if (!p.images.length) throw new VeniceError('At least one input image is required for editing.', 400, 'no_input_images');
  const format = p.format || 'png';
  const body: Record<string, unknown> = {
    modelId: p.model,
    prompt: p.prompt,
    images: p.images,
    output_format: format,
    aspect_ratio: p.aspectRatio || 'auto',
    safe_mode: p.safeMode ?? true,
  };
  if (p.resolution) body.resolution = p.resolution;
  if (p.quality) body.quality = p.quality;

  const { res, startedAt } = await postJson('/image/multi-edit', p.apiKey, body, IMAGE_SLOW_HINT);
  const contentType = res.headers.get('content-type') || '';
  if (!res.ok) {
    const { text, json } = await readOrLost(() => readBody(res), startedAt, IMAGE_SLOW_HINT);
    throw toVeniceError(res, json, text);
  }
  if (contentType.includes('application/json')) {
    const { text, json } = await readOrLost(() => readBody(res), startedAt, IMAGE_SLOW_HINT);
    const first = Array.isArray(json?.images) ? json.images[0] : undefined;
    if (typeof first === 'string' && first) return first.startsWith('data:') ? first : `data:image/${format};base64,${first}`;
    throw toVeniceError(res, json, text);
  }
  const blob = await readOrLost(() => res.blob(), startedAt, IMAGE_SLOW_HINT);
  if (!blob.size) throw new VeniceError('Venice returned an empty image.', res.status, 'empty_image');
  return blobToDataUrl(blob);
}

// ── video ────────────────────────────────────────────────────────────────────

export interface VeniceVideoQueueParams {
  apiKey: string;
  /** Exact Venice model id, e.g. "seedance-2-5-text-to-video-basic" */
  model: string;
  prompt?: string;
  negativePrompt?: string;
  /** "4s" … "30s" (per-model list in GET /models) */
  duration: string;
  /** "480p" | "720p" | "1080p" | "4k" (per-model) */
  resolution?: string;
  /** Omit for image-to-video models (they derive it from the frame) */
  aspectRatio?: string;
  /** Seedance: generate audio (default true) */
  audio?: boolean;
  /** image-to-video first frame: http(s) URL or data URL */
  imageUrl?: string;
  /** optional last frame for models that support transitions */
  endImageUrl?: string;
  /** reference-to-video: up to 30 URLs / data URLs */
  referenceImageUrls?: string[];
  /** Background music for models with audio input: one WAV/MP3 URL or data URL (≤30s, ≤15MB) */
  audioUrl?: string;
  /** Voice / sound-effect donors (reference-to-video): WAV/MP3 clips; Seedance 2.0 up to 3 of 2–15s (≤15s in total), 2.5 up to 10 of 2–30s (≤30s) */
  referenceAudioUrls?: string[];
  /** Reference clips (reference-to-video): MP4/MOV URLs or data URLs, ≤50MB each; Seedance 2.0 up to 3 of 2–15s (≤15s in total), 2.5 up to 10 of 2–30s (≤30s) */
  referenceVideoUrls?: string[];
  /** Kling O3 reference mode: characters and objects to keep the same, named @Element1… in the prompt */
  elements?: { frontalImageUrl: string; referenceImageUrls?: string[] }[];
  /** Kling O3 reference mode: up to 4 pictures of the scene, named @Image1… in the prompt */
  sceneImageUrls?: string[];
  /** Seedance 2.5 task hint when reference clips are present; Venice infers it from the prompt when omitted */
  omniReferenceTaskType?: 'auto' | 'reference' | 'edit' | 'extend';
  consents?: { seedance: VeniceSeedanceConsents };
}

export interface VeniceVideoQueued {
  queueId: string;
  model: string;
  /** Only for VPS-backed models; when present the retrieve endpoint returns JSON only */
  downloadUrl?: string;
}

export async function veniceQueueVideo(p: VeniceVideoQueueParams): Promise<VeniceVideoQueued> {
  const body: Record<string, unknown> = {
    model: p.model,
    duration: p.duration,
  };
  if (p.prompt) body.prompt = p.prompt;
  if (p.negativePrompt) body.negative_prompt = p.negativePrompt;
  if (p.resolution) body.resolution = p.resolution;
  if (p.aspectRatio) body.aspect_ratio = p.aspectRatio;
  if (typeof p.audio === 'boolean') body.audio = p.audio;
  if (p.imageUrl) body.image_url = p.imageUrl;
  if (p.endImageUrl) body.end_image_url = p.endImageUrl;
  if (p.referenceImageUrls?.length) body.reference_image_urls = p.referenceImageUrls.slice(0, 30);
  if (p.audioUrl) body.audio_url = p.audioUrl;
  if (p.referenceAudioUrls?.length) body.reference_audio_urls = p.referenceAudioUrls.slice(0, 10);
  if (p.referenceVideoUrls?.length) body.reference_video_urls = p.referenceVideoUrls.slice(0, 10);
  if (p.elements?.length) {
    body.elements = p.elements.slice(0, 4).map(element => ({
      frontal_image_url: element.frontalImageUrl,
      ...(element.referenceImageUrls?.length ? { reference_image_urls: element.referenceImageUrls.slice(0, 3) } : {}),
    }));
  }
  if (p.sceneImageUrls?.length) body.scene_image_urls = p.sceneImageUrls.slice(0, 4);
  if (p.omniReferenceTaskType) body.omni_reference_task_type = p.omniReferenceTaskType;
  if (p.consents) body.consents = p.consents;

  const { res } = await postJson('/video/queue', p.apiKey, body);
  const { text, json } = await readBody(res);
  if (!res.ok || looksLikeConsentRequest(json)) throw toVeniceError(res, json, text);
  if (!json?.queue_id) throw new VeniceError('Venice did not return a queue id.', res.status, 'no_queue_id', json);
  return { queueId: String(json.queue_id), model: String(json.model || p.model), downloadUrl: json.download_url };
}

export type VeniceVideoStatus =
  | { status: 'PROCESSING'; averageExecutionTime: number; executionDuration: number }
  | { status: 'COMPLETED'; blob: Blob };

export interface VeniceVideoRetrieveParams {
  apiKey: string;
  model: string;
  queueId: string;
  /** Pre-signed URL returned by /video/queue for VPS-backed models */
  downloadUrl?: string;
  deleteMediaOnCompletion?: boolean;
  /** Bytes received of the finished video as it streams back (total is null when unknown). */
  onProgress?: (receivedBytes: number, totalBytes: number | null) => void;
}

/** How long to wait for a response to start. */
const VENICE_HEADERS_TIMEOUT_MS = 90 * 1000;
/** A body is abandoned only when no bytes arrive for this long; a slow but flowing download keeps going. */
const VENICE_IDLE_TIMEOUT_MS = 90 * 1000;

const timeoutError = (message: string): DOMException => new DOMException(message, 'TimeoutError');

/** fetch that gives up when the headers do not arrive in time; the body is bounded separately. */
async function fetchWithHeaderTimeout(input: string, init: RequestInit, controller: AbortController): Promise<Response> {
  const timer = setTimeout(() => controller.abort(timeoutError('Venice did not respond in time.')), VENICE_HEADERS_TIMEOUT_MS);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** Read a streamed body into a Blob, failing only when the stream stalls for `idleMs`. */
async function readBodyWithIdleTimeout(
  res: Response,
  controller: AbortController,
  idleMs: number,
  onProgress?: (receivedBytes: number, totalBytes: number | null) => void,
): Promise<Blob> {
  const type = res.headers.get('content-type') || 'video/mp4';
  if (!res.body) return res.blob();
  const totalHeader = res.headers.get('content-length');
  const total = totalHeader ? Number(totalHeader) || null : null;
  const reader = res.body.getReader();
  const chunks: BlobPart[] = [];
  let received = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => controller.abort(timeoutError('The video download stalled.')), idleMs);
  };
  // A real fetch body rejects its pending read when the request is aborted; racing the
  // abort signal as well keeps this correct for any stream.
  const aborted = new Promise<never>((_, reject) => {
    controller.signal.addEventListener('abort', () => reject(controller.signal.reason ?? timeoutError('The video download stalled.')), { once: true });
  });
  try {
    arm();
    for (;;) {
      const { done, value } = await Promise.race([reader.read(), aborted]);
      if (done) break;
      if (value) {
        chunks.push(value as BlobPart);
        received += value.byteLength;
        onProgress?.(received, total);
      }
      arm();
    }
  } catch (e) {
    reader.cancel().catch(() => undefined);
    throw e;
  } finally {
    if (timer) clearTimeout(timer);
  }
  return new Blob(chunks, { type });
}

/** Poll a queued job. Resolves to the mp4 blob once the job is COMPLETED. */
export async function veniceRetrieveVideo(p: VeniceVideoRetrieveParams): Promise<VeniceVideoStatus> {
  const controller = new AbortController();
  const res = await fetchWithHeaderTimeout(`${VENICE_API_BASE}/video/retrieve`, {
    method: 'POST',
    headers: { ...authHeaders(p.apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: p.model,
      queue_id: p.queueId,
      delete_media_on_completion: p.deleteMediaOnCompletion ?? false,
    }),
  }, controller);
  const contentType = res.headers.get('content-type') || '';
  if (!res.ok) {
    const { text, json } = await readBody(res);
    throw toVeniceError(res, json, text);
  }
  if (contentType.startsWith('video/') || contentType === 'application/octet-stream') {
    const blob = await readBodyWithIdleTimeout(res, controller, VENICE_IDLE_TIMEOUT_MS, p.onProgress);
    if (!blob.size) throw new VeniceError('Venice returned an empty video file.', res.status, 'empty_video');
    return { status: 'COMPLETED', blob };
  }
  const { text, json } = await readBody(res);
  if (!json) throw new VeniceError(`Unexpected response from Venice: ${text.slice(0, 120)}`, res.status, 'bad_response');
  if (json.status === 'COMPLETED') {
    if (!p.downloadUrl) throw new VeniceError('Video completed but no download URL was provided.', res.status, 'no_download_url', json);
    // VPS-backed models hand back a pre-signed URL on another host; a CSP or CORS
    // block surfaces here as a TypeError, so name the host in the message.
    const downloadController = new AbortController();
    let dl: Response;
    try {
      dl = await fetchWithHeaderTimeout(p.downloadUrl, {}, downloadController);
    } catch (e) {
      if ((e as { name?: string } | null)?.name === 'TimeoutError') throw e;
      let host = 'the download host';
      try { host = new URL(p.downloadUrl).host; } catch { /* keep default */ }
      throw new VeniceError(
        `Could not download the finished video from ${host} (blocked by browser policy or CORS). Open the download link to save it manually.`,
        0,
        'download_blocked',
        { downloadUrl: p.downloadUrl },
      );
    }
    if (!dl.ok) throw new VeniceError(`Failed to download the finished video (${dl.status}).`, dl.status, 'download_failed', { downloadUrl: p.downloadUrl });
    const blob = await readBodyWithIdleTimeout(dl, downloadController, VENICE_IDLE_TIMEOUT_MS, p.onProgress);
    return { status: 'COMPLETED', blob };
  }
  if (json.status === 'FAILED' || json.status === 'ERROR') {
    throw new VeniceError(extractMessage(json, 'Video generation failed.'), res.status, 'generation_failed', json);
  }
  return {
    status: 'PROCESSING',
    averageExecutionTime: Number(json.average_execution_time) || 0,
    executionDuration: Number(json.execution_duration) || 0,
  };
}

export interface VeniceVideoQuoteParams {
  apiKey: string;
  model: string;
  duration: string;
  resolution?: string;
  aspectRatio?: string;
  audio?: boolean;
  /** Total seconds of reference clips sent with the job; prices the "input with video" tier */
  referenceVideoTotalDuration?: number;
}

/** Price estimate in USD for a queue request with the same inputs. */
export async function veniceVideoQuote(p: VeniceVideoQuoteParams): Promise<number> {
  const body: Record<string, unknown> = { model: p.model, duration: p.duration };
  if (p.resolution) body.resolution = p.resolution;
  if (p.aspectRatio) body.aspect_ratio = p.aspectRatio;
  if (typeof p.audio === 'boolean') body.audio = p.audio;
  if (p.referenceVideoTotalDuration && p.referenceVideoTotalDuration > 0) body.reference_video_total_duration = p.referenceVideoTotalDuration;
  const res = await fetch(`${VENICE_API_BASE}/video/quote`, {
    method: 'POST',
    headers: { ...authHeaders(p.apiKey), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const { text, json } = await readBody(res);
  if (!res.ok) throw toVeniceError(res, json, text);
  const quote = Number(json?.quote);
  if (!Number.isFinite(quote)) throw new VeniceError('Venice returned an invalid quote.', res.status, 'bad_quote', json);
  return quote;
}

/** Best-effort cleanup of Venice-side storage once the mp4 has been saved. Never throws. */
export async function veniceCompleteVideo(p: { apiKey: string; model: string; queueId: string }): Promise<void> {
  try {
    await fetch(`${VENICE_API_BASE}/video/complete`, {
      method: 'POST',
      headers: { ...authHeaders(p.apiKey), 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: p.model, queue_id: p.queueId }),
    });
  } catch {
    /* ignore */
  }
}

/** Progress heuristic from the retrieve payload (P80 estimate vs elapsed), capped so it never claims done. */
export function veniceProgressPercent(status: VeniceVideoStatus): number {
  if (status.status === 'COMPLETED') return 100;
  if (!status.averageExecutionTime) return 0;
  return Math.max(0, Math.min(95, Math.round((status.executionDuration / status.averageExecutionTime) * 100)));
}

// ── balance ──────────────────────────────────────────────────────────────────

/** What the key has left to spend (GET /api_keys/rate_limits, or /billing/balance). */
export interface VeniceBalance {
  /** Whether requests can be paid for now. */
  canConsume: boolean;
  /** What requests are paid from: USD, DIEM (staking), bundled or earned credits. */
  currency: 'USD' | 'VCU' | 'DIEM' | 'BUNDLED_CREDITS' | 'EARNED_CREDITS' | null;
  usd: number | null;
  /** DIEM left in the current epoch (a day), out of `diemAllocation`; null when not staking. */
  diem: number | null;
  diemAllocation: number;
  bundledCredits: number | null;
  earnedCredits: number | null;
}

const numberOrNull = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const getJson = async (path: string, apiKey: string): Promise<{ res: Response; json: any; text: string }> => {
  let res: Response;
  try {
    res = await fetch(`${VENICE_API_BASE}${path}`, { headers: authHeaders(apiKey) });
  } catch {
    throw new VeniceError('Could not reach Venice to read the balance. Check your connection.', 0, 'network');
  }
  const { text, json } = await readBody(res);
  return { res, json, text };
};

/**
 * Reads what the key has left. GET /api_keys/rate_limits works with every kind of key, the
 * Inference Only keys apps use included, and gives the key's own balances; GET
 * /billing/balance is only asked when that endpoint is not there (it refuses those keys).
 */
export async function veniceBalance(apiKey: string): Promise<VeniceBalance> {
  const limits = await getJson('/api_keys/rate_limits', apiKey);
  if (limits.res.ok) {
    const data = limits.json?.data ?? {};
    const usd = numberOrNull(data.balances?.USD);
    const diem = numberOrNull(data.balances?.DIEM);
    const bundled = numberOrNull(data.balances?.BUNDLED_CREDITS);
    const charged = data.apiTier?.isCharged !== false;
    const anything = [usd, diem, bundled].some(value => (value ?? 0) > 0);
    return {
      canConsume: data.accessPermitted !== false && (anything || !charged),
      // Venice spends a staking allowance first, then bundled credits, then dollars
      currency: (diem ?? 0) > 0 ? 'DIEM' : (bundled ?? 0) > 0 ? 'BUNDLED_CREDITS' : 'USD',
      usd,
      diem,
      diemAllocation: 0,
      bundledCredits: bundled,
      earnedCredits: null,
    };
  }
  if (limits.res.status === 401) {
    const detail = extractMessage(limits.json, '');
    throw new VeniceError(
      `Venice did not accept this key${detail ? ` (${detail})` : ''}. Paste it again in full, without quotes, spaces or a "Bearer" prefix.`,
      401,
      'invalid_key',
      limits.json,
    );
  }
  const { res, json } = await getJson('/billing/balance', apiKey);
  if (!res.ok) throw toVeniceError(limits.res, limits.json, limits.text); // the first refusal says more
  const currencies = ['USD', 'VCU', 'DIEM', 'BUNDLED_CREDITS', 'EARNED_CREDITS'];
  return {
    canConsume: json?.canConsume !== false,
    currency: currencies.includes(json?.consumptionCurrency) ? json.consumptionCurrency : null,
    usd: numberOrNull(json?.balances?.usd),
    diem: numberOrNull(json?.balances?.diem),
    diemAllocation: numberOrNull(json?.diemEpochAllocation) ?? 0,
    bundledCredits: numberOrNull(json?.balances?.bundledCredits),
    earnedCredits: numberOrNull(json?.balances?.earnedCredits),
  };
}

const money = (value: number) => `$${value.toFixed(value >= 100 ? 0 : 2)}`;
const amount = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(2));

/**
 * The balance as people read it: what requests are paid from now, and how much of it is
 * left. `short` is for a node's footer, `full` names everything the account holds.
 */
export const describeVeniceBalance = (b: VeniceBalance): { short: string; full: string } => {
  const parts: string[] = [];
  if (b.usd !== null) parts.push(`${money(b.usd)} USD`);
  // Only for someone who stakes: the key endpoint reports 0 DIEM for everyone else
  if (b.diem !== null && (b.diem > 0 || b.diemAllocation > 0)) parts.push(`${amount(b.diem)}${b.diemAllocation ? ` of ${amount(b.diemAllocation)}` : ''} DIEM left today`);
  if (b.bundledCredits) parts.push(`${money(b.bundledCredits)} bundled credits`);
  if (b.earnedCredits) parts.push(`${amount(b.earnedCredits)} earned credits`);
  const full = parts.length > 0 ? parts.join(' · ') : 'No balance';
  let short: string;
  if (b.currency === 'DIEM' && b.diem !== null) short = `${amount(b.diem)} DIEM left`;
  else if (b.currency === 'BUNDLED_CREDITS' && b.bundledCredits !== null) short = `${money(b.bundledCredits)} credits left`;
  else if (b.currency === 'EARNED_CREDITS' && b.earnedCredits !== null) short = `${amount(b.earnedCredits)} credits left`;
  else if (b.usd !== null) short = `${money(b.usd)} left`;
  else short = full;
  if (!b.canConsume) short = 'Out of balance';
  return { short, full };
};

/**
 * How much of what requests are paid from is left, in dollars, when that can be told
 * (USD, or DIEM, where 1 DIEM pays for $1 a day); null otherwise.
 */
export const spendableUsd = (b: VeniceBalance): number | null => {
  if (b.currency === 'DIEM') return b.diem;
  if (b.currency === 'BUNDLED_CREDITS') return b.bundledCredits;
  if (b.currency === 'USD' || b.currency === null) return b.usd;
  return null;
};

// ── Text ─────────────────────────────────────────────────────────────────────

export interface VeniceChatParams {
  apiKey: string;
  /** A text model id from the catalog, e.g. "claude-sonnet-5-5" or "openai-gpt-4o-mini-2024-07-18" */
  model: string;
  system: string;
  prompt: string;
  /** The answer's length allowed, in tokens; a thinking model gets room to think on top (see thinkingAllowance) */
  maxTokens?: number;
  temperature?: number;
  /** The model thinks before it answers (the catalog's supportsReasoning): its thinking is paid for and needs room */
  reasoning?: boolean;
  /** How hard it thinks, one of the catalog's reasoningEffortOptions ("low", "medium", "high", ...) */
  reasoningEffort?: string;
}

/**
 * How many tokens a thinking model may spend thinking, by effort, on top of the answer. Venice
 * counts the thinking against max_completion_tokens as OpenAI does; a cap set for the answer
 * alone is used up by the thinking, and the model then writes nothing at all (GPT-6 Astra Pro
 * at the Think up card's 1,400, 2026-10-12).
 */
export const thinkingAllowance = (effort: string | undefined): number => {
  switch (effort) {
    case 'none': return 0;
    case 'minimal': case 'low': return 2_000;
    case 'medium': return 6_000;
    case 'xhigh': return 24_000;
    case 'max': return 48_000;
    default: return 12_000; // "high", and a model that does not say
  }
};

export interface VeniceChatAnswer {
  text: string;
  /** What the answer was billed for, as Venice counted it */
  inputTokens: number | null;
  outputTokens: number | null;
}

/**
 * One answer from a text model, through Venice's OpenAI-style chat endpoint. Venice's own
 * system prompt is left out so only ours shapes the writing, and a thinking model's
 * reasoning is stripped from the answer.
 */
export async function veniceChat(p: VeniceChatParams): Promise<VeniceChatAnswer> {
  const maxTokens = p.maxTokens ?? 900;
  const allowance = p.reasoning ? thinkingAllowance(p.reasoningEffort) : 0;
  const body: Record<string, unknown> = {
    model: p.model,
    messages: [{ role: 'system', content: p.system }, { role: 'user', content: p.prompt }],
    temperature: p.temperature ?? 0.9,
    max_completion_tokens: maxTokens + allowance,
    ...(p.reasoning && p.reasoningEffort ? { reasoning_effort: p.reasoningEffort } : {}),
    venice_parameters: { include_venice_system_prompt: false, strip_thinking_response: true },
  };
  const { res, startedAt } = await postJson('/chat/completions', p.apiKey, body);
  const { text, json } = await readOrLost(() => readBody(res), startedAt);
  if (!res.ok) throw toVeniceError(res, json, text);
  const content = json?.choices?.[0]?.message?.content;
  const answer = (typeof content === 'string' ? content : Array.isArray(content) ? content.map((part: any) => part?.text || '').join('') : '').trim();
  if (!answer) {
    // A thinking model that ran out of room says so: its thinking used the tokens the answer needed
    const finish = json?.choices?.[0]?.finish_reason;
    const thought = json?.usage?.completion_tokens_details?.reasoning_tokens;
    const spent = typeof thought === 'number' ? thought : typeof json?.usage?.completion_tokens === 'number' ? json.usage.completion_tokens : null;
    if (finish === 'length' || (typeof thought === 'number' && thought > 0)) {
      const tokens = spent !== null ? ` (${spent.toLocaleString('en-US')} tokens)` : '';
      throw new VeniceError(`The model used its whole allowance thinking${tokens} and wrote nothing. Ask for a shorter answer, or lower its thinking.`, res.status, 'no_text', json);
    }
    throw new VeniceError('Venice returned no text.', res.status, 'no_text', json);
  }
  const usage = json?.usage ?? {};
  return {
    text: answer,
    inputTokens: typeof usage.prompt_tokens === 'number' ? usage.prompt_tokens : null,
    outputTokens: typeof usage.completion_tokens === 'number' ? usage.completion_tokens : null,
  };
}

export interface VeniceTextModel {
  id: string;
  name: string;
  /** Dollars per million tokens */
  inputUsd: number | null;
  outputUsd: number | null;
  contextTokens: number | null;
  /** The model thinks before it answers; the thinking is billed as output and needs room of its own */
  reasoning?: boolean;
  /** The efforts it can be asked to think with, as the catalog names them ("low", "medium", "high", ...) */
  efforts?: string[];
  /** The effort it takes when none is asked for */
  defaultEffort?: string;
}

/**
 * The text models Venice serves now, from its public catalog (no key needed). Read once per
 * session and kept in sessionStorage; what is offline is left out.
 */
export async function veniceTextModels(): Promise<VeniceTextModel[]> {
  const cacheKey = 'venice_text_models_v2';
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached) as VeniceTextModel[];
  } catch { /* no session storage */ }
  const res = await fetch(`${VENICE_API_BASE}/models?type=text`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new VeniceError(`Venice's model list could not be read (${res.status}).`, res.status, 'models');
  const json = await res.json();
  const models: VeniceTextModel[] = (Array.isArray(json?.data) ? json.data : [])
    .filter((m: any) => m && typeof m.id === 'string' && m.model_spec?.offline !== true)
    .map((m: any) => ({
      id: m.id,
      name: typeof m.model_spec?.name === 'string' ? m.model_spec.name : m.id,
      inputUsd: typeof m.model_spec?.pricing?.input?.usd === 'number' ? m.model_spec.pricing.input.usd : null,
      outputUsd: typeof m.model_spec?.pricing?.output?.usd === 'number' ? m.model_spec.pricing.output.usd : null,
      contextTokens: typeof m.model_spec?.availableContextTokens === 'number' ? m.model_spec.availableContextTokens : null,
      // A model that thinks before it answers, and the efforts it takes (the catalog's capabilities)
      ...(m.model_spec?.capabilities?.supportsReasoning === true ? {
        reasoning: true,
        efforts: Array.isArray(m.model_spec.capabilities.reasoningEffortOptions) ? m.model_spec.capabilities.reasoningEffortOptions.filter((e: unknown) => typeof e === 'string') : [],
        ...(typeof m.model_spec.capabilities.defaultReasoningEffort === 'string' ? { defaultEffort: m.model_spec.capabilities.defaultReasoningEffort } : {}),
      } : {}),
    }));
  try { sessionStorage.setItem(cacheKey, JSON.stringify(models)); } catch { /* full or absent */ }
  return models;
}
